// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from "!mapbox-gl"
import Box from "@mui/material/Box"
import Skeleton from "@mui/material/Skeleton"
import { styled } from "@mui/material/styles"
import * as turf from "@turf/turf"
import { useEffect, useRef, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../../../common/axios"
import {
  addImageLayerToMap,
  removeImage,
  removeInteraction,
  removeLayer,
  removeSource,
  useMapLoader,
} from "../../../../../common/mapbox-utils"
import { transactionSoldPriceDisplay } from "../../../../../common/utils"
import "./map-static/markers.css"
import TransactionIcon from "./map-static/transaction.png"

export default function GeoInsightsTab({
  mapTransactions,
  isLoadingMapTransactions,
  mapRelatedOffices,
  isLoadingMapRelatedOffices,
}) {
  const mapContainer = useRef()
  const { map, mapLoaded } = useMapLoader(mapContainer)

  const DATA_SOURCE_IDS = {
    transactions: "transactions",
  }
  const IMAGE_NAMES = {
    transaction: "transaction",
  }
  const LAYER_IDS = {
    transactions: "transactions",
    transactionClusters: "transaction-clusters",
    transactionClusterCount: "transaction-cluster-count",
  }
  const INTERACTION_IDS = {
    clusterClick: "click-clusters",
    transactionClick: "click-transaction",
  }

  useEffect(
    () => {
      if (!mapLoaded) {
        return
      }
      const transactionsGeoJSON = {
        type: "FeatureCollection",
        features: featuresFromItems(mapTransactions),
      }
      map.current.addSource(DATA_SOURCE_IDS.transactions, {
        type: "geojson",
        data: transactionsGeoJSON,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      })

      addImageLayerToMap(
        map,
        TransactionIcon,
        IMAGE_NAMES.transaction,
        DATA_SOURCE_IDS.transactions,
        LAYER_IDS.transactions,
        0.45,
        ["!", ["has", "point_count"]]
      )

      map.current.addInteraction(INTERACTION_IDS.transactionClick, {
        type: "click",
        target: { layerId: LAYER_IDS.transactions },
        handler: (e) => {
          const coordinates = e.feature.geometry.coordinates.slice()
          const properties = e.feature.properties
          function TitleValue(title, value) {
            return `<li><strong>${title}</strong>: <span>${value}</span></li>`
          }
          const html = `
                  <ul class="popup">
                  ${TitleValue("City", properties.city)}
                  ${TitleValue("Address", properties.address)}
                  ${TitleValue("Zipcode", properties.zipcode)}
                  ${TitleValue("DOM", properties.days_on_market)}
                  ${
                    properties.sold_price
                      ? TitleValue(
                          "Price sold",
                          transactionSoldPriceDisplay(properties)
                        )
                      : ""
                  }
                  </ul>
                `
          new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(html)
            .addTo(map.current)
        },
      })

      addImageLayerToMap(
        map,
        TransactionIcon,
        IMAGE_NAMES.transaction,
        DATA_SOURCE_IDS.transactions,
        LAYER_IDS.transactionClusters,
        0.45,
        ["has", "point_count"]
      )

      map.current.addLayer({
        id: LAYER_IDS.transactionClusterCount,
        type: "symbol",
        source: DATA_SOURCE_IDS.transactions,
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
          "text-color": "black",
          "text-variable-anchor": ["bottom-left"],
        },
        paint: {
          "text-halo-color": "white",
          "text-halo-width": 4,
        },
      })

      map.current.addInteraction(INTERACTION_IDS.clusterClick, {
        type: "click",
        target: { layerId: LAYER_IDS.transactionClusters },
        handler: (e) => {
          const features = map.current.queryRenderedFeatures(e.point, {
            layers: [LAYER_IDS.transactionClusters],
          })
          const clusterId = features[0].properties.cluster_id
          map.current
            .getSource(DATA_SOURCE_IDS.transactions)
            .getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (err) return

              map.current.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom,
              })
            })
        },
      })

      mapRelatedOffices.map(({ to_office: office }) => {
        const officeImage = document.createElement("div")
        officeImage.className = `marker marker-${
          office.is_current ? "current" : "prev"
        }-office`
        const popup = new mapboxgl.Popup().setText(office.name)
        return new mapboxgl.Marker(officeImage)
          .setLngLat(office.location.coordinates)
          .setPopup(popup)
          .addTo(map.current)
      })

      const fullFeatureCollection = {
        ...transactionsGeoJSON,
        features: [
          ...transactionsGeoJSON.features,
          ...featuresFromItems(
            mapRelatedOffices.map((relatedOffice) => relatedOffice.to_office)
          ),
        ],
      }
      if (fullFeatureCollection.features.length > 0) {
        const zoomedOutGeoJSON = turf.buffer(fullFeatureCollection, 100, {
          units: "miles",
        })
        const bbox = turf.bbox(zoomedOutGeoJSON)
        map.current.fitBounds(bbox)
      }

      return () => {
        removeLayer(map, LAYER_IDS.transactions)
        removeLayer(map, LAYER_IDS.transactionClusters)
        removeLayer(map, LAYER_IDS.transactionClusterCount)
        removeInteraction(map, INTERACTION_IDS.clusterClick)
        removeInteraction(map, INTERACTION_IDS.transactionClick)
        removeImage(map, IMAGE_NAMES.transaction)
        removeSource(map, DATA_SOURCE_IDS.transactions)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapLoaded]
  )

  const isLoading =
    !mapLoaded || isLoadingMapTransactions || isLoadingMapRelatedOffices
  return (
    <Box sx={{ position: "relative", height: "calc(100vh - 380px)" }}>
      {isLoading && (
        <SkeletonBox>
          <Skeleton variant="rectangular" height="100%" width="100%" />
        </SkeletonBox>
      )}
      <Box ref={mapContainer} sx={{ height: "100%" }} />
    </Box>
  )
}

function featuresFromItems(items) {
  return items.map((item) => ({
    type: "Feature",
    geometry: item.location,
    properties: item,
  }))
}

export function useGeoInsightsTabState(ssotAgentID) {
  const [mapTransactions, setMapTransactions] = useState([])
  const [isLoadingMapTransactions, setIsLoadingMapTransactions] =
    useState(false)
  const [mapRelatedOffices, setMapRelatedOffices] = useState([])
  const [isLoadingMapRelatedOffices, setIsLoadingMapRelatedOffices] =
    useState(false)

  useLoadMapData(
    ssotAgentID,
    `/realitydb/api/candidate-profile/tx/map/${ssotAgentID}/`,
    setMapTransactions,
    setIsLoadingMapTransactions
  )

  useLoadMapData(
    ssotAgentID,
    `/realitydb/api/candidate-profile/related-offices/map/${ssotAgentID}/`,
    setMapRelatedOffices,
    setIsLoadingMapRelatedOffices
  )

  return {
    mapTransactions,
    isLoadingMapTransactions,
    mapRelatedOffices,
    isLoadingMapRelatedOffices,
  }
}

function useLoadMapData(ssotAgentID, apiURL, setData, setIsLoading) {
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      if (!ssotAgentID) {
        return
      }
      setIsLoading(true)
      axios
        .get(apiURL)
        .then(({ data }) => setData(data))
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ssotAgentID]
  )
}

const SkeletonBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "background.paper",
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
}))
